/**
 * 이용 범위 — **선택 이용 매장에 무엇이 열려 있는가**의 SSOT.
 *
 * (2026-08-19 `src/lib/externalEdition.ts` 로 신설 → 2026-08-22 shared 로 이사
 *  → 2026-08-28·29 두 번 뜻과 이름을 고쳤다. 파일·심볼 이름은 그대로 둔다 — 바꾸면 40여
 *  파일이 한꺼번에 흔들리는데 얻는 건 이름뿐이라, 값·문서·화면 문구만 옮겼다.)
 *
 * ## 범위가 둘인 이유
 * 이 앱은 원래 우리(라인업)가 우리 일을 하려고 만든 물건이라, 고객 관리·개통·정산·정책·재고·
 * 관리자 콘솔이 한 벌로 다 들어 있다. 전부를 열 상대와 일부만 열 상대가 있다.
 *   · 전체 이용(full)    : **전 기능.** 우리 매장도, 계약한 판매점도 여기.
 *   · 선택 이용(partial) : **기본이 "아무것도 없음"** 이고, 아래 카탈로그에 올렸고
 *                          **관리자가 켜 둔** 것만 보인다.
 *
 * ## ⚠️ 이 축을 «회원이냐 아니냐»로 읽지 말 것 — 두 번 틀렸다
 * ① 처음엔 "우리 회사(internal)냐 고객사(external)냐" 였다. 고객사엔 축소판을 판다는 뜻.
 *    → 2026-08-28 폐기. **계약한 고객은 우리와 똑같이 전 기능을 쓴다**로 뒤집혔다.
 * ② 그다음엔 "정식(full)이냐 체험(trial)이냐" 였다. → **하루 만에 틀린 게 드러났다**(08-29).
 *    스캐너 캠페인 회원들은 **정회원인데 신분증 스캔 하나만** 쓴다. 체험이 아니다.
 *
 * 두 번 다 같은 실수였다 — **사람의 신분을 이 칸으로 표현하려 한 것.** 신분은 역할
 * (`profiles.role`)이 정하고, 이 칸은 **그 그릇이 어디까지 쓰나**만 정한다. 둘은 독립이다:
 * 정회원 + 선택 이용이 정상적인 조합이고, 그게 지금 회원 11명이다.
 * 값의 역사는 아래 `Edition` 타입 주석에, 별칭 처리는 `resolveEdition` 에 있다.
 *
 * ## 층이 둘이다 — 카탈로그(코드) / 스위치(DB)
 * 처음엔 이 파일의 배열 하나가 전부였다. 2026-08-22 에 "관리자가 화면에서 골라 켠다"가
 * 필요해지면서 층을 갈랐다.
 *
 *   ① **카탈로그 = 이 파일** — "외부용에 **나갈 수 있는** 후보". 경로·이름·부르는 API 를 적는다.
 *      바꾸려면 커밋이 필요하다(리뷰·이력이 남는다).
 *   ② **스위치 = DB(`external_feature_toggles`)** — 그중 **지금 켜 줄 것**. 관리자가 클릭으로 바꾼다.
 *
 * 카탈로그까지 DB 로 내리지 않은 이유가 셋이다.
 *   · DB 에 경로를 자유롭게 적을 수 있으면 관리자가 `/admin/crawling` 을 **한 번 잘못 켜는 순간**
 *     우리 운영 콘솔이 고객에게 열린다. 카탈로그가 코드에 있으면 관리자는 "열어도 된다고 이미
 *     판정된 것들" 중에서만 고른다 — 위험한 선택지가 화면에 아예 없다.
 *   · 경로를 DB 에 저장하면 나중에 경로를 리팩터링하는 순간 그 행은 아무 데도 안 가리키는
 *     유령이 된다(에러도 안 나고 조용히 사라진다). 그래서 **DB 에는 `key` 만** 저장한다.
 *     이건 LNB 배치 커스터마이즈가 메뉴를 복제하지 않고 "배치 힌트"만 저장하는 것과 같은
 *     이유다(docs/spec/ui-sidebar-layout.md).
 *   · 서버 잠금(§아래)이 매 요청 DB 를 읽어야 하고, 그 캐시가 낡는 순간이 곧 보안 구멍이 된다.
 *
 * ## 왜 "기본이 빈 화면"인가 — 이게 이 파일의 핵심이다
 * 반대로 짤 수도 있었다("이것만 숨긴다"). 그러면 앞으로 새 화면이 생길 때마다 만든 사람이
 * "이거 외부에도 나가나?"를 기억해야 하고, 한 번 잊는 순간 우리 내부 화면이 고객 앱에
 * 그대로 새어 나간다. **잊으면 새는 설계**는 언젠가 반드시 샌다.
 * 그래서 방향을 뒤집었다 — 새 화면은 만들어도 외부용에 안 나온다. 나오게 하려면 이 파일에
 * 한 줄을 **일부러** 적고, 관리자가 **일부러** 켜야 한다. 잊으면 안 나올 뿐, 사고는 안 난다.
 *
 * 이 원칙은 DB 층에도 그대로 산다 — **토글 행이 없으면 꺼짐**이다. 카탈로그에 후보를 추가해도
 * 저절로 노출되지 않는다.
 *
 * ## 화면과 서버가 같은 목록을 본다 (2026-08-22)
 * 예전엔 이 목록이 `src/` 아래 있어서 **화면만** 볼 수 있었고, 문서에도 "이건 화면 울타리이지
 * 보안 경계가 아니다"라고 적혀 있었다. 내부 직원끼리 쓸 땐 맞는 트레이드오프였지만 **외부
 * 고객에게 파는 앱**에서는 다르다 — 개발자도구로 API 를 직접 부르면 화면 울타리는 아무 일도
 * 하지 않는다.
 * 그래서 이 파일을 `shared/` 로 옮겨 **서버 미들웨어(server/src/lib/external-edition-guard.ts)가
 * 같은 카탈로그를 읽어** 외부용 계정의 요청을 허용 목록 밖에서 막는다. 화면 목록과 API 목록이
 * 따로 놀면 반드시 어긋나므로, 한 파일에 나란히 적는다(`path` 옆의 `apis`).
 */

/** 외부용 카탈로그의 한 항목 — "외부용에 나갈 수 있는 기능" 하나. */
export interface ExternalFeature {
  /**
   * 이 기능을 가리키는 이름표. **DB 토글·문서·스크린챗 요청이 이 값으로 부른다.**
   * 경로가 바뀌어도 이 값은 그대로 둔다 — 바꾸면 켜 둔 설정이 꺼진다.
   */
  key: string;
  /** 화면 주소. 이 경로와 그 아래(`/x/y` 형태)가 함께 열린다. */
  path: string;
  /** LNB 에 뜨는 이름. */
  name: string;
  /** 무슨 기능인지 한 줄. 메뉴에는 안 나오고 관리 화면·문서에서 쓴다. */
  description: string;
  /**
   * 이 화면이 부르는 **API 접두어**들(`/api/v2/...`). 서버 잠금이 이걸 본다.
   *
   * ⚠️ 여기에 적는 건 "이 기능 전용 창구"여야 한다. 내부 화면과 공유하는 넓은 창구
   * (`/api/v2/crm/*` 같은)를 적으면 그 창구 전체가 외부에 열린다. 공유하고 싶으면 창구를
   * 좁게 새로 파는 게 맞다 — 신분증 스캔이 `crm/id-photo` 와 몸통을 공유하면서도 창구는
   * `tools/id-scan` 으로 따로 낸 것이 그 예다.
   */
  apis: string[];
}

/**
 * 외부용에 **나갈 수 있는** 기능 전부(카탈로그).
 *
 * ⚠️ 여기 있다고 켜져 있는 게 아니다 — 실제 노출은 DB 토글(`external_feature_toggles`)이
 * 정한다. 관리 화면은 `/admin/external-features`.
 */
export const EXTERNAL_FEATURE_CATALOG: ExternalFeature[] = [
  {
    key: "id-card-scan",
    path: "/tools/id-card-scan",
    name: "신분증 스캔",
    description:
      "비스듬히 찍은 신분증 사진을 반듯하게 펴서 A4 실물 크기 스캔본으로 만든다 — 인쇄하거나 사진 파일로 내려받는다.",
    apis: ["/api/v2/tools/id-scan"],
  },
];

/** LNB 에서 외부용 기능들을 담는 묶음 이름. */
export const EXTERNAL_NAV_GROUP_LABEL = "도구";

/**
 * 카탈로그 안의 모든 key — 관리 화면이 "고를 수 있는 전부"를 그릴 때, 서버가 "모르는 key 무시"
 * 를 판정할 때 쓴다.
 */
export function externalFeatureKeys(): string[] {
  return EXTERNAL_FEATURE_CATALOG.map((f) => f.key);
}

/**
 * 켜진 key 들로 **지금 외부용에 실제로 열린 기능** 목록을 만든다.
 *
 * 카탈로그 순서를 지킨다 — 첫 항목이 로그인 직후 착지점이 되므로 순서가 DB 저장 순서에 따라
 * 흔들리면 안 된다. **카탈로그에 없는 key 는 조용히 버린다**(기능을 걷어냈는데 토글 행이 남아
 * 있는 경우 — 유령 메뉴를 만들지 않는다).
 */
export function enabledExternalFeatures(enabledKeys: readonly string[]): ExternalFeature[] {
  const on = new Set(enabledKeys);
  return EXTERNAL_FEATURE_CATALOG.filter((f) => on.has(f.key));
}

/**
 * 기능 목록과 무관하게 외부용에서도 늘 열리는 **화면 경로**.
 *
 * 여기 있는 건 "기능"이 아니라 앱을 쓰려면 반드시 필요한 자리다 — 로그인 직후 착지점을
 * 계산하는 뿌리(/), 그리고 본인 계정 화면. 여기에 업무 화면을 추가하지 말 것(그건 카탈로그다).
 */
const ALWAYS_ALLOWED_PATHS = ["/", "/profile"];

/**
 * 기능과 무관하게 외부용 계정에게도 늘 열리는 **API**.
 *
 * 로그인·내가 누구인지·개인설정·로그아웃처럼 **앱이 뜨지도 못하게 되는** 창구만 둔다.
 * 업무 데이터를 주는 창구를 여기 추가하지 말 것 — 그건 카탈로그 항목의 `apis` 다.
 */
const ALWAYS_ALLOWED_APIS = [
  "/api/v2/auth", // 로그인·내 프로필(/me)·비밀번호 변경·로그아웃
  "/api/v2/me", // 개인 UI 설정(모달 크기·화면 배율 등)
  "/api/v2/activity/events", // 화면 이동 기록 — 안 보내면 프론트가 계속 실패 재시도한다
];

function matches(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * 외부용 모드에서 이 **화면 주소**를 열어도 되나.
 *
 * `enabledKeys` 는 관리자가 켜 둔 기능들. 빈 배열이면 뿌리·본인 계정 말고는 아무것도 안 열린다
 * (= "기본이 빈 화면"이 DB 층에서도 지켜진다).
 */
export function isExternalPathAllowed(pathname: string, enabledKeys: readonly string[]): boolean {
  if (ALWAYS_ALLOWED_PATHS.includes(pathname)) return true;
  return enabledExternalFeatures(enabledKeys).some((f) => matches(pathname, f.path));
}

/**
 * 외부용 계정이 이 **API 경로**를 불러도 되나 — 서버 잠금의 판정식.
 *
 * 화면 통과(`isExternalPathAllowed`)와 **같은 카탈로그**를 본다. 둘이 갈라지면 화면은 막혔는데
 * API 는 열려 있는(또는 그 반대의) 상태가 조용히 생긴다.
 */
export function isExternalApiAllowed(pathname: string, enabledKeys: readonly string[]): boolean {
  if (ALWAYS_ALLOWED_APIS.some((base) => matches(pathname, base))) return true;
  return enabledExternalFeatures(enabledKeys).some((f) =>
    f.apis.some((base) => matches(pathname, base)),
  );
}

/**
 * 로그인 직후·막힌 주소에서 되돌릴 착지점 — **켜진 것 중 첫 기능**.
 *
 * 하나도 안 켜져 있으면 뿌리("/")다. 그 경우 화면은 "열린 기능이 없습니다" 안내를 띄운다
 * (뿌리로 보내면 HomeRedirect 가 다시 여기를 불러 무한 왕복이 되므로, 부르는 쪽이 처리한다).
 */
export function externalHomePath(enabledKeys: readonly string[]): string {
  return enabledExternalFeatures(enabledKeys)[0]?.path ?? "/";
}

// ─── 누가 외부용을 보나 ────────────────────────────────────────────────────
//
// 진본은 **DB 칸**(`tenants.edition`)이다. 아래 시각 기준선은 그 칸이 비었을 때만 쓰는
// **폴백**이다(2026-08-22 격하 — 원래는 이게 유일한 판정이었다).
//
// 🕳️ 2026-08-19 실사고: 처음엔 "라인업·본사 관리자가 아니면 전부 외부용"으로 냈다. 배포하는
//    순간 **이미 쓰고 있던 판매점들의 화면이 통째로 사라졌다** — 고객 관리도 개통도 안 보이고
//    신분증 스캔 하나만 남았다. 외부용은 "앞으로 팔 물건"인데, 그 선을 신분(라인업이냐 아니냐)
//    으로 그으니 **어제까지 쓰던 사람들이 전부 신규 고객 취급**을 받은 것이다.
//    그래서 선을 **시간**으로 다시 그었다 — 이 시각보다 먼저 만들어진 계정은 쓰던 화면 그대로.
//
// 🕳️ 그런데 시간만으로는 곧 막힌다(2026-08-22): 가입 시각은 "언제 왔나"이지 "누구인가"가
//    아니다. **라인업에 새 직원이 들어오면 그 계정은 기준선 이후라 외부용으로 떨어진다.**
//    파트너 계정·데모 계정·기존 매장의 직원 추가도 전부 같다. 그래서 판정을 매장 칸으로 옮기고
//    (백필은 이 기준선 그대로 했으므로 그날 동작은 한 톨도 안 바뀌었다) 시각은 폴백으로 남겼다.
//
// ⚠️ **이 값을 과거로 옮기지 말 것.** 옮기는 순간 칸이 빈 계정 중 그 사이에 가입한 실사용
//    매장들의 화면이 사라진다(위 사고의 재현). 앞으로 미루는 것은 안전하다.
export const EXTERNAL_EDITION_SINCE = "2026-08-20T00:00:00+09:00";

/**
 * 매장의 **이용 범위**. DB(`tenants.edition`)가 진본.
 *
 * - `full`    = **전체 이용** — 상품 전 기능. 우리(라인업) 매장도, 계약한 판매점도 여기.
 * - `partial` = **선택 이용** — 켜 준 기능만. 아래 카탈로그에서 관리자가 고른 것만 보인다.
 *
 * ## ⚠️ 이 축은 «회원이냐 아니냐»가 아니다 (2026-08-29 정정)
 * 2026-08-28 에 잠깐 `trial`(체험회원)이라 불렀는데, 그 이름이 곧 틀린 게 드러났다 —
 * **스캐너 캠페인 회원들은 정회원인데 기능만 좁게 쓴다.** 체험이 아니다.
 * 그래서 축의 이름을 «회원 등급»에서 **«이용 범위»** 로 고쳤다. 사람이 조직에서 무엇인지는
 * 역할(`profiles.role`)이 정하고, 그 그릇이 어디까지 쓰는지는 이 칸이 정한다 — **두 축은
 * 서로 독립이다.** 정회원이면서 선택 이용일 수 있고, 그게 지금 스캐너 회원 11명이다.
 *
 * 값의 역사: `internal`/`external`(~2026-08-28) → `full`/`trial`(2026-08-28) →
 * `full`/`partial`(2026-08-29). **옛 값은 전부 `resolveEdition` 이 별칭으로 받는다.**
 */
export type Edition = "full" | "partial";

/**
 * 이 계정이 외부 배포판 대상인가.
 *
 * **DB 칸이 먼저, 없으면 가입 시각 폴백, 그것도 모르면 내부용.**
 * 잘못 판단했을 때 "새 고객이 잠깐 전체 화면을 본다"는 되돌릴 수 있지만, "쓰던 매장의 화면이
 * 사라진다"는 그 자리에서 업무가 멈추는 사고다. 그래서 모를 때는 안전한 쪽(내부용)으로 기운다.
 */
export function resolveEdition(
  tenantEdition: string | null | undefined,
  createdAt: string | null | undefined,
): Edition {
  if (tenantEdition === "partial") return "partial";
  if (tenantEdition === "full") return "full";
  // 옛 값 별칭 — 코드와 DB 백필의 배포 순서가 어긋나도 안 깨지게 두 세대를 다 받는다.
  //   trial    (2026-08-28 하루짜리 이름) = 켜 준 것만 → partial
  //   external (~2026-08-28 옛 이름)      = 켜 준 것만 → partial
  //   internal (~2026-08-28 옛 이름)      = 전부       → full
  if (tenantEdition === "trial" || tenantEdition === "external") return "partial";
  if (tenantEdition === "internal") return "full";
  // 칸이 비었다 — 매장에 안 속한 전역 관리자이거나, 아직 백필이 안 닿은 행이다.
  if (!createdAt) return "full";
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return "full";
  return t >= Date.parse(EXTERNAL_EDITION_SINCE) ? "partial" : "full";
}
